import React from 'react';
import { Box, Typography, Button, Stack, Divider, Paper, Chip, CircularProgress } from '@mui/material';
import { CheckCircle, Print, AddShoppingCart, Stars } from '@mui/icons-material';
import WhatsAppSection from './WhatsAppBillSection';
import ThermalReceipt80mm from '../receipt/ThermalReceipt80mm';
import EinvoiceStatus from '../../common/EinvoiceStatus';

// Shown after a bill is paid: totals, thermal receipt preview, Print, New Sale
// and Send on WhatsApp. Used by the normal checkout and the gift voucher sale
// checkout, which passes an order-shaped object with document_kind
// 'gift_voucher_sale' (see VoucherCheckoutPage) and its status as children.

const fmt = (v) =>
  `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── Printer connection dot ── */
const printerDot = {
  connected:    { color: '#16A34A', label: 'Printer ready' },
  connecting:   { color: '#D97706', label: 'Connecting to printer…' },
  disconnected: { color: '#94A3B8', label: 'Printer app not found' },
  error:        { color: '#DC2626', label: 'Printer app not found' },
};

export const SuccessOverlay = ({ order, receipt, isReceiptLoading, onNewSale, onPrint, printerStatus, customerWhatsappOptIn, children }) => {
  const changeDue      = parseFloat(order?.change_due || 0);
  const grandTotal     = parseFloat(order?.grand_total || 0);
  const amountPaid     = parseFloat(order?.amount_paid || 0);
  const loyaltyEarned  = parseFloat(order?.loyalty_points_earned || 0);
  const loyaltyRedeemed = parseFloat(order?.loyalty_points_redeemed || 0);
  const loyaltyBalance = order?.customer_loyalty_points_balance;

  return (
    <Box sx={{
      position: 'absolute', inset: 0, zIndex: 20,
      bgcolor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: { xs: 2, sm: 3 },
    }}>
      <Paper sx={{ borderRadius: { xs: '14px', sm: '16px' }, width: '100%', maxWidth: { xs: 340, sm: 520 }, maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <Box sx={{ flexShrink: 0, bgcolor: '#F0FDF4', borderBottom: '1px solid #BBF7D0', px: { xs: 2, sm: 2.5 }, py: { xs: 1.75, sm: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircle sx={{ fontSize: { xs: 30, sm: 36 }, color: '#16A34A', flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#14532D', lineHeight: 1.2 }}>Payment Successful</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#4B7155', fontWeight: 500 }}>{order?.order_number}</Typography>
          </Box>
          {order?.customer_name && (
            <Chip label={order.customer_name} size="small" sx={{ flexShrink: 0, height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5', maxWidth: 100, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
          )}
        </Box>

        {changeDue > 0 && (
          <Box sx={{ flexShrink: 0, bgcolor: '#FFFBEB', borderBottom: '1px solid #FDE68A', px: { xs: 2, sm: 2.5 }, py: { xs: 1.1, sm: 1.25 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#92400E', letterSpacing: '0.04em' }}>CHANGE DUE</Typography>
            <Typography sx={{ fontSize: { xs: '1.45rem', sm: '1.65rem' }, fontWeight: 900, color: '#B45309', lineHeight: 1 }}>{fmt(changeDue)}</Typography>
          </Box>
        )}

        <Box sx={{ flexShrink: 0, px: { xs: 2, sm: 2.5 }, py: { xs: 1.25, sm: 1.25 } }}>
          <Stack spacing={0.85}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>Total Charged</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{fmt(grandTotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>Amount Received</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{fmt(amountPaid)}</Typography>
            </Box>
            {(loyaltyEarned > 0 || loyaltyRedeemed > 0) && (
              <>
                <Divider sx={{ my: 0.25 }} />
                {loyaltyEarned > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Stars sx={{ color: '#F59E0B', fontSize: 16 }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#D97706' }}>
                      +{loyaltyEarned.toFixed(0)} loyalty points earned
                    </Typography>
                  </Stack>
                )}
                {loyaltyBalance != null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>Points Balance</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#D97706' }}>
                      {Math.round(parseFloat(loyaltyBalance)).toLocaleString('en-IN')} pts
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Box>

        {children}

        {/* GST e-invoice status — read-only here. This app has no route to
            trigger/retry generation (admin-only); status only, matching what
            actually just happened at payment: pending (or "not enabled" if
            einvoice_auto_generate is off, since the job never auto-fires). */}
        {order?.customer_gstin && (
          <>
            <Divider />
            <Box sx={{ flexShrink: 0, px: { xs: 2, sm: 2.5 }, py: { xs: 1.1, sm: 1.25 } }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.06em', mb: 0.75 }}>
                GST E-INVOICE
              </Typography>
              <Stack spacing={0.6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>IRN Status</Typography>
                  <EinvoiceStatus status={order?.e_invoice?.status} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>E-Way Bill</Typography>
                  <EinvoiceStatus status={order?.eway_bill?.status} />
                </Box>
              </Stack>
              <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 0.6, lineHeight: 1.5 }}>
                Generation runs in the background — check Order History in a moment.
              </Typography>
            </Box>
          </>
        )}

        <Divider />

        <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: { xs: 1.5, sm: 2 }, py: 1.5, bgcolor: '#F8FAFC' }}>
          <Typography sx={{ flexShrink: 0, fontSize: '0.68rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.06em', mb: 1 }}>
            RECEIPT PREVIEW
          </Typography>
          <Box sx={{
            bgcolor: '#E2E8F0',
            borderRadius: '12px',
            py: 1.5,
            px: { xs: 0.5, sm: 1.5 },
            display: 'flex',
            justifyContent: 'center',
            flexGrow: 1,
            minHeight: 0,
            maxHeight: { xs: 300, sm: 360 },
            overflow: 'auto',
          }}>
            {isReceiptLoading ? (
              <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size={20} />
                <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                  Loading receipt preview...
                </Typography>
              </Stack>
            ) : receipt ? (
              <Box sx={{
                transformOrigin: 'top center',
                transform: { xs: 'scale(0.92)', sm: 'scale(1)' },
                width: '80mm',
                flexShrink: 0,
              }}>
                <ThermalReceipt80mm receipt={receipt} />
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.78rem', color: '#64748B', py: 6 }}>
                Receipt preview unavailable.
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {printerStatus && (
          <Box sx={{ flexShrink: 0, px: { xs: 1.5, sm: 2 }, pt: 0.75, pb: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: (printerDot[printerStatus] || printerDot.disconnected).color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 500 }}>
              {(printerDot[printerStatus] || printerDot.disconnected).label}
            </Typography>
          </Box>
        )}
        <Box sx={{ flexShrink: 0, px: { xs: 1.5, sm: 2 }, py: { xs: 1.25, sm: 1.5 }, display: 'flex', gap: 1 }}>
          <Button variant="outlined" fullWidth startIcon={<Print sx={{ fontSize: '0.95rem !important' }} />} onClick={onPrint} disabled={!receipt}
            sx={{ flex: 1, py: { xs: 0.9, sm: 1 }, borderRadius: '8px', fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.82rem' }, textTransform: 'none', borderColor: '#E2E8F0', color: '#475569', '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' } }}>
            Print
          </Button>
          <Button variant="contained" disableElevation fullWidth startIcon={<AddShoppingCart sx={{ fontSize: '0.95rem !important' }} />} onClick={onNewSale}
            sx={{ flex: 2, py: { xs: 0.9, sm: 1 }, borderRadius: '8px', fontWeight: 800, fontSize: { xs: '0.82rem', sm: '0.88rem' }, textTransform: 'none', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
            New Sale
          </Button>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <WhatsAppSection order={order} receipt={receipt} customerWhatsappOptIn={customerWhatsappOptIn} source="checkout" />
        </Box>

      </Paper>
    </Box>
  );
};

export { WhatsAppSection };
export default SuccessOverlay;
